// sitelen sike — a round shorthand script for toki pona.
//
// One glyph per syllable: the consonant is a stroke shape, the vowel is a
// ROTATION of that shape (at e everything points/opens down, at o up;
// a = home, i = a flipped, u = a + a dot), and a final -n is a bar under
// the glyph. On top of that sits a brief system: the ~137 word lexicon is
// compressed onto first-syllable "keys", each key seating up to four words
// on a mark ladder (bare / +M1 dot above / +M2 slash below / both).
//
// This module is pure data + geometry: phonology, the brief table, word
// encoding, glyph geometry as structured data, and drill helpers.
// Rendering to actual SVG elements happens in components (see
// src/components/sitelenSike/). Regenerate BRIEFS/DISPLACED with
// scripts/gen_briefs_sike.py if the lexicon or frequency list changes.

export const CONSONANTS = 'ptksmnljw';
export const VOWELS = 'aeiou';

// Syllables that never occur in toki pona phonology.
export const ILLEGAL_SYLLABLES = ['ji', 'ti', 'wo', 'wu'];

// ---------------------------------------------------------------- letterforms

// Each consonant is stroke primitives in a 100x100 box, "a" orientation.
// {d} = SVG path, {c:[cx,cy,r]} = loop circle (a loop anywhere = nasal).
// '' is the bare-vowel comma carrier.
export const SHAPES = {
  p: [{ d: 'M72,22 A34,34 0 1 0 72,78' }],                                  // open curve
  m: [{ d: 'M72,26 A34,34 0 1 0 72,78' }, { c: [74, 19, 8] }],              // curve + loop
  w: [{ d: 'M72,22 A34,34 0 1 0 72,78' }, { d: 'M72,78 L57,63' }],          // curve + tick
  t: [{ d: 'M62,22 L62,78' }, { d: 'M62,50 L20,50' }],                      // the key
  n: [{ d: 'M58,20 L58,80' }, { d: 'M58,24 L32,33' }, { c: [24, 36, 7] }],  // looped pennant
  s: [{ d: 'M33,24 L72,24 L38,80' }],                                       // the 7
  l: [{ d: 'M52,20 L52,70 Q52,83 37,80' }],                                 // the hook
  k: [{ d: 'M72,22 L34,50 L72,78' }],                                       // chevron
  j: [{ d: 'M72,22 L34,50 L72,78 L85,67' }],                                // chevron + tail
  '': [{ d: 'M56,32 Q65,50 45,72' }],                                       // bare-vowel comma
};

// Vowel = rotation. Reading rule: at e everything points/opens DOWN,
// at o everything points/opens UP; a = home, i = a flipped, u = a + dot.
// s, l, t, n rotate counterclockwise to obey; the rest go clockwise.
export const ROT = { a: 0, e: 90, i: 180, o: 270, u: 0 };
export const ROT_SWAP = {
  s: { e: 270, o: 90 }, l: { e: 270, o: 90 },
  t: { e: 270, o: 90 }, n: { e: 270, o: 90 },
};

// The glyph box shared by every renderer: shapes live in 0..100, the u-dot
// hangs off the right edge, marks live in the headroom above and below.
export const VIEW_BOX = '-8 -20 120 155';
export const GLYPH_W = 120;
export const GLYPH_H = 155;

// ---------------------------------------------------------------- phonology

// Parse a toki pona word into syllables ["ten","po"]. Coda -n stays inside
// its syllable (the script writes codas inside the glyph).
export function syllabify(word) {
  const w = word.toLowerCase();
  const out = [];
  let i = 0;
  while (i < w.length) {
    let s = '';
    if (!VOWELS.includes(w[i])) { s += w[i]; i += 1; }
    if (i < w.length && VOWELS.includes(w[i])) { s += w[i]; i += 1; }
    if (i < w.length && w[i] === 'n' && (i + 1 === w.length || !VOWELS.includes(w[i + 1]))) {
      s += 'n';
      i += 1;
    }
    out.push(s);
  }
  return out;
}

// "ten" -> { cons:'t', vowel:'e', coda:true } · "a" -> { cons:'', vowel:'a' }
export function parseSyllable(syl) {
  const coda = syl.length > 1 && syl.endsWith('n');
  const core = coda ? syl.slice(0, -1) : syl;
  const cons = VOWELS.includes(core[0]) ? '' : core[0];
  return { cons, vowel: core.slice(cons.length), coda };
}

// A word the script can actually write: every syllable is (C)V(n) over the
// toki pona alphabet. Case-insensitive so names still validate.
export function isValidWord(word) {
  if (!/^[a-z]+$/i.test(word)) return false;
  return syllabify(word).every((s) => {
    const { cons, vowel, coda } = parseSyllable(s);
    if (!VOWELS.includes(vowel)) return false;
    if (cons && !CONSONANTS.includes(cons)) return false;
    return s === cons + vowel + (coda ? 'n' : '');
  });
}

// ---------------------------------------------------------------- brief table

// ladder[i] pairs with MARKS[i]. Keyed on true first syllables; rescue
// rule: overflow words brief on their next syllable with room (DISPLACED
// lists them). Generated by scripts/gen_briefs_sike.py — keep one line so
// the generator's --inject can splice a regenerated table back in.
export const MARKS = ['bare', '+M1', '+M2', '+M1+M2'];
export const BRIEFS = {"li": ["li", "lili", "lipu"], "mi": ["mi", "mije", "misikeke"], "e": ["e", "esun"], "ni": ["ni", "nimi"], "la": ["la", "lawa", "lape", "laso"], "po": ["pona", "poka", "poki"], "to": ["toki", "tomo"], "si": ["sina", "sitelen", "sike", "sijelo"], "jan": ["jan"], "lon": ["lon"], "ta": ["tawa", "taso"], "pi": ["pi", "pilin", "pini", "pipi"], "wi": ["wile"], "so": ["sona", "soweli", "soko"], "ken": ["ken"], "ka": ["kama", "kalama", "kala", "kasi"], "ten": ["tenpo"], "i": ["ike", "ilo", "ijo"], "se": ["seme", "sewi", "selo", "seli"], "o": ["o", "ona", "olin", "open"], "ma": ["ma", "mama", "mani"], "pa": ["pali", "pana", "pakala", "palisa"], "su": ["suli", "suno", "supa", "suwi"], "mo": ["moku", "moli"], "lu": ["lukin", "luka", "lupa"], "kin": ["kin"], "wen": ["awen"], "en": ["en"], "sa": ["sama", "alasa"], "wa": ["wawa", "walo", "waso"], "we": ["weka"], "te": ["telo"], "ke": ["kepeken", "akesi"], "ki": ["kili", "kiwen", "kipisi", "kijetesantakalu"], "kon": ["kon"], "jo": ["jo"], "in": ["insa"], "an": ["anpa", "ante"], "mon": ["monsi", "monsuta"], "na": ["nasin", "nasa", "namako"], "sin": ["sin", "sinpin"], "tan": ["tan"], "tu": ["tu"], "wan": ["wan"], "no": ["noka"], "u": ["uta", "utala"], "ko": ["ko", "oko"], "len": ["len"], "lin": ["linja"], "me": ["meli", "pimeja"], "mun": ["mun"], "mu": ["mu", "mute", "musi"], "nan": ["nanpa"], "ne": ["nena"], "pan": ["pan"], "un": ["unpa"], "ja": ["jaki"], "je": ["jelo"], "le": ["lete", "leko"], "lo": ["loje"], "pu": ["pu"], "ton": ["tonsi"], "ku": ["ku", "kulupu", "kute", "kule"], "a": ["a", "ala", "ale", "anu"]};
export const DISPLACED = {"awen": "wen", "akesi": "ke", "alasa": "sa", "pimeja": "me", "oko": "ko"};

// Every briefed word, ladder order — the deck for whole-word drills.
export const WORDS = Object.values(BRIEFS).flat();

// word -> its seat { key, mark }, computed once.
const SEAT = (() => {
  const m = new Map();
  for (const [key, ladder] of Object.entries(BRIEFS)) {
    ladder.forEach((w, i) => m.set(w, { key, mark: i }));
  }
  return m;
})();

export function seatOf(word) {
  return SEAT.get(word.toLowerCase()) || null;
}

// ---------------------------------------------------------------- encoding

// encodeWord("tenpo")    -> { type:'brief', key:'ten', mark:0 }
// encodeWord("pipi")     -> { type:'brief', key:'pi', mark:3 }
// encodeWord("kokosila") -> { type:'spelled', sylls:['ko','ko','si','la'] }  (overline)
// encodeWord("Sonja")    -> { type:'name', sylls:['son','ja'] }              (cartouche)
export function encodeWord(word, opts = {}) {
  if (opts.name || /^[A-Z]/.test(word)) return { type: 'name', sylls: syllabify(word) };
  const seat = SEAT.get(word.toLowerCase());
  if (seat) return { type: 'brief', ...seat };
  return { type: 'spelled', sylls: syllabify(word) };
}

// mark index -> which decorations a brief glyph wears.
export function markFlags(mark) {
  return { m1: mark === 1 || mark === 3, m2: mark >= 2 };
}

// ---------------------------------------------------------------- geometry

// Everything a renderer needs to draw one syllable glyph, as data:
// body shapes + rotation, and which decorations to add. spec is
// { cons, vowel, coda, m1, m2 }.
export function glyphGeometry(spec) {
  const { cons, vowel } = spec;
  const rot = (ROT_SWAP[cons] && ROT_SWAP[cons][vowel]) ?? ROT[vowel] ?? 0;
  return {
    rot,
    shapes: SHAPES[cons] ?? SHAPES[''],
    dot: vowel === 'u',
    coda: !!spec.coda,
    m1: !!spec.m1,
    m2: !!spec.m2,
  };
}

// Latin spelling of one spec, e.g. {cons:'t', vowel:'o', coda:true} -> "ton".
export function specToLatin(spec) {
  return `${spec.cons || ''}${spec.vowel}${spec.coda ? 'n' : ''}`;
}

// All 46 legal syllable specs (9 consonants x 5 vowels minus the 4 that
// never occur, plus the 5 bare vowels) — the flashcard deck for drills.
export function legalSyllables() {
  const out = [];
  for (const v of VOWELS) out.push({ cons: '', vowel: v, coda: false });
  for (const c of CONSONANTS) {
    for (const v of VOWELS) {
      if (!ILLEGAL_SYLLABLES.includes(c + v)) out.push({ cons: c, vowel: v, coda: false });
    }
  }
  return out;
}

// ---------------------------------------------------------------- practice

// How confusable two syllable specs are, for building mean multiple-choice
// distractors. In this script the traps are: same shape at a different
// rotation (same cons, other vowel), and different shapes at the same
// rotation — plus the a/u pair, identical but for the dot.
export function specSimilarity(a, b) {
  let s = 0;
  if (a.cons === b.cons) s += 2;
  if (a.vowel === b.vowel) s += 2;
  const rotA = glyphGeometry(a).rot;
  const rotB = glyphGeometry(b).rot;
  if (rotA === rotB) s += 1;
  if ((a.coda || false) === (b.coda || false)) s += 1;
  return s;
}

// Pick `count` distractor syllables that look similar to `answer`.
// rng is an injected () => [0,1) so tests can be deterministic.
export function syllableDistractors(answer, rng, count = 3) {
  const pool = legalSyllables().filter((s) => specToLatin(s) !== specToLatin(answer));
  return pool
    .map((s) => ({ s, score: specSimilarity(s, answer) + rng() * 2 }))
    .sort((x, y) => y.score - x.score)
    .slice(0, count)
    .map((x) => x.s);
}

// How confusable two briefed words are: sharing a key is the whole trap
// (only the marks differ), then keys that look alike, then same mark.
export function wordSimilarity(a, b) {
  const sa = seatOf(a);
  const sb = seatOf(b);
  if (!sa || !sb) return 0;
  let score = 0;
  if (sa.key === sb.key) score += 6;
  else score += specSimilarity(parseSyllable(sa.key), parseSyllable(sb.key));
  if (sa.mark === sb.mark) score += 1;
  return score;
}

// Pick `count` distractor words that could plausibly be misread as
// `answer` — ladder-mates first, then look-alike keys.
export function wordDistractors(answer, rng, count = 3) {
  return WORDS
    .filter((w) => w !== answer)
    .map((w) => ({ w, score: wordSimilarity(w, answer) + rng() * 3 }))
    .sort((x, y) => y.score - x.score)
    .slice(0, count)
    .map((x) => x.w);
}

// Fisher–Yates with injected rng.
export function shuffleWith(arr, rng) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
